import { Box, Stack } from '@mui/material';

import { CalculateFestivalCo2 } from '@/domain/impact-calculate-festival/entities/calculate-festival-co2.entity';
import { CalculateFestivalEnv } from '@/domain/impact-calculate-festival/entities/calculate-festival-env.entity';
import { CalculateFestivalEss } from '@/domain/impact-calculate-festival/entities/calculate-festival-ess.entity';
import {
  selectImpactCalculateFestivalEnvExtraValuesForCurrentProject,
  selectImpactCalculateFestivalEnvForProject,
  selectImpactCalculateFestivalEssForProject,
} from '@/domain/impact-calculate-festival/impact-calculate-festival.selectors';
import { selectImpactCalculateFestivalCo2ForProject } from '@/domain/impact-calculate-festival/impact-calculate-festival.selectors';
import { References } from '@/domain/impact-references/entities/references.entity';
import { selectImpactReferences } from '@/domain/impact-references/references.selectors';
import { ProjectFestival } from '@/domain/project-festival/entities/project-festival.entity';
import { useAppSelector } from '@/ui/hook/store';
import { ShowDetailsMode } from '@/ui/pages/app/components/project-page/ProjectScores';
import {
  ProjectScoresDetailsData,
  ProjectScoresDetailsItemParams,
} from '@/ui/pages/app/components/project-page/ProjectScoresDetails/ProjectScoresDetails.types';
import { ProjectScoresDetailsCard } from '@/ui/pages/app/components/project-page/ProjectScoresDetails/ProjectScoresDetailsCard';
import { ProjectScoresDetailsTitle } from '@/ui/pages/app/components/project-page/ProjectScoresDetails/ProjectScoresDetailsTitle';

import { prepareProjectFestivalScoreDetailsDataCo2 } from './utils/prepare-project-score-details-data/co2';
import { prepareProjectFestivalScoreDetailsDataEnv } from './utils/prepare-project-score-details-data/env';
import { prepareProjectFestivalScoreDetailsDataEss } from './utils/prepare-project-score-details-data/ess';

export function ProjectFestivalScoresDetails({
  project,
  showDetailsMode,
  onClickDetailsItem,
}: {
  project: ProjectFestival;
  showDetailsMode: ShowDetailsMode;
  onClickDetailsItem: (params: ProjectScoresDetailsItemParams) => void;
}) {
  const calculateCo2 = useAppSelector(
    selectImpactCalculateFestivalCo2ForProject(project.id),
  );
  const calculateEnv = useAppSelector(
    selectImpactCalculateFestivalEnvForProject(project.id),
  );
  const calculateEss = useAppSelector(
    selectImpactCalculateFestivalEssForProject(project.id),
  );
  const references = useAppSelector(selectImpactReferences);

  const { valueKgeCO2ByParticipant, valueRefKgeCO2ByParticipant } =
    useAppSelector(
      selectImpactCalculateFestivalEnvExtraValuesForCurrentProject,
    );

  const data: ProjectScoresDetailsData[] = prepareData(
    showDetailsMode,
    calculateCo2,
    calculateEnv,
    calculateEss,
    references,
    valueKgeCO2ByParticipant,
    valueRefKgeCO2ByParticipant,
  );

  if (data === null) {
    return <></>;
  }
  return (
    <Stack spacing={2}>
      {data.map((x) => (
        <Box key={x.title}>
          <ProjectScoresDetailsTitle title={x.title} />
          <ProjectScoresDetailsCard
            data={x}
            onClickDetailsItem={onClickDetailsItem}
          />
        </Box>
      ))}
    </Stack>
  );
}

function prepareData(
  showDetailsMode: ShowDetailsMode,
  calculateCo2: CalculateFestivalCo2,
  calculateEnv: CalculateFestivalEnv,
  calculateEss: CalculateFestivalEss,
  references: References,
  valueKgeCO2ByParticipant: number,
  valueRefKgeCO2ByParticipant: number,
): ProjectScoresDetailsData[] {
  switch (showDetailsMode) {
    case 'none':
      return [];
    case 'co2':
      return prepareProjectFestivalScoreDetailsDataCo2(
        calculateCo2,
        references,
      );
    case 'env':
      return prepareProjectFestivalScoreDetailsDataEnv(
        calculateEnv,
        valueKgeCO2ByParticipant,
        valueRefKgeCO2ByParticipant,
      );
    case 'ess':
      return prepareProjectFestivalScoreDetailsDataEss(calculateEss);
  }
}
